import type { ComponentPropsWithoutRef } from "react";

import { twMerge } from "tailwind-merge";

import { useEvent } from "shared/hooks/useEvent";

import type { TableProps, TableRowExt, TableSort } from "./types";

import { ThSmall, TrTdSmall } from "./ui";

export const SmallTable = <ColumnKey extends string, Row extends TableRowExt<ColumnKey>>({
  className,
  columns,
  data,
  hovered,
  keyTrFormatter,
  onRowClick,
  onSortChange,
  sort,
}: TableProps<ColumnKey, Row>) => {
  const onSort = useEvent((sort: TableSort<ColumnKey>) => {
    onSortChange?.(sort);
  });
  const onRowClickMemo = useEvent((row: Row) => {
    onRowClick?.(row);
  });

  const hasTh = columns.some((el) => !!el.title);

  return (
    <TableUISmall className={className}>
      {hasTh && (
        <thead>
          <tr>
            {columns.map((column) => (
              <ThSmall column={column} key={column.key} onSortChange={onSort} sort={sort} />
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {data.map((row, i) => (
          <TrTdSmall
            columns={columns}
            hasTh={hasTh}
            hovered={hovered}
            key={keyTrFormatter?.(row, i) || i}
            onRowClick={onRowClick ? onRowClickMemo : undefined}
            row={row}
          />
        ))}
      </tbody>
    </TableUISmall>
  );
};

export const TableUISmall = (props: ComponentPropsWithoutRef<"table">) => {
  return (
    <table
      {...props}
      className={twMerge("relative w-full border-separate border-spacing-0", props.className)}
    />
  );
};
